<!--
  SupportTicketView.svelte — one ticket, as its requester (or the owner of the
  licence it was filed under) sees it.

  Everything the page offers is the server's decision, rendered as-is through
  `permissions`: the composer is live only when `canReply`, and otherwise stays
  on screen, disabled, with the reason — a lapsed licence, a closed ticket, or a
  licence owner reading a seat holder's ticket they may not answer. Closing and
  rating stay available after a licence lapses, so a customer can always finish
  a conversation even when they cannot continue it.

  A closed ticket takes no replies. It offers a follow-up instead — a new ticket
  carrying `followUpOfKey` — so the history of the old one stays as it ended.

  The People card lists the owner and anyone they added. The owner manages that
  list; a participant sees a note saying who brought them in, may reply while
  the owner's support lasts, and may leave — after which the ticket is out of
  their sight, so the page returns to `/Support`.
-->
<script lang="ts">
	import { untrack } from 'svelte';
	import { Button, Spinner } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import {
		ConfirmButton,
		EmptyState,
		LicenseAppIcon,
		PageHeader,
		RelativeTime,
		RoutedButton,
		SupportCommunityHelp,
		SupportAttachmentsPanel,
		SupportComposer,
		SupportMessageThread,
		SupportPeopleCard,
		SupportPriorityBadge,
		SupportRating,
		SupportStatusBadge,
		SupportTicketLinks
	} from '../../components';
	import type { SupportComposerMessage } from '../../components/SupportComposer.svelte';
	import { push } from '../../abstract-spa-router';
	import {
		addSupportParticipant,
		closeSupportTicket,
		getSupportOverview,
		getSupportTicket,
		rateSupportTicket,
		removeSupportParticipant,
		reopenSupportTicket,
		replySupportTicket
	} from '../../AppNetwork';
	import type { SupportCommunityLinks, SupportPerson, SupportTicket } from '../../AppTypes';
	import { tokenData } from '../../Auth';
	import { supportReasonText } from '../../support/supportFormat';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	interface Props {
		/** Route params — which ticket. */
		params?: { ticketKey?: string };
	}

	let { params = {} }: Props = $props();

	/** The ticket on screen. */
	let ticket = $state<SupportTicket | null>(null);
	/** Whether it is loading. */
	let loading = $state(true);
	/** Failure of the read. */
	let error = $state('');
	/** Whether a reply is being sent. */
	let sending = $state(false);
	/** Fraction of a reply's attachments uploaded, while it is sending; `null` otherwise. */
	let uploadProgress = $state<number | null>(null);
	/** Whether a close or reopen is in flight. */
	let changing = $state(false);
	/** Whether a rating is being submitted. */
	let rating = $state(false);
	/** Whether a change to who is on the ticket is in flight. */
	let changingPeople = $state(false);
	/** Where to point a reader whose support lapsed or never existed. Loaded only then. */
	let communityLinks = $state<SupportCommunityLinks | null>(null);

	/** Refusals that are about the licence, which the community can stand in for. */
	const LICENSE_REASONS = ['no-license', 'no-entitlement', 'expired', 'not-configured'];

	/** Loads the ticket. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** The ticket carries its thread and what the reader may do with it. */
		try {
			ticket = await getSupportTicket(params.ticketKey as string);
			/* The overview is fetched only when there is a licence refusal to soften. */
			if (LICENSE_REASONS.includes(ticket.permissions.replyBlockedReason ?? '')) void loadCommunityLinks();
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load the ticket');
		} finally {
			loading = false;
		}
	}

	/** Loads the community links. Best-effort: without them the refusal still reads correctly. */
	async function loadCommunityLinks(): Promise<void> {
		/** A failure only costs the pointer, never the ticket. */
		try {
			communityLinks = (await getSupportOverview()).communityLinks;
		} catch {
			communityLinks = null;
		}
	}

	/** Sends a reply. Resolves whether it went, so the composer knows to clear. */
	async function send(message: SupportComposerMessage): Promise<boolean> {
		sending = true;
		/* Only a send with files shows a bar; without, the button's spinner is enough. */
		uploadProgress = message.files.length > 0 ? 0 : null;
		const onProgress = message.files.length > 0 ? (fraction: number) => (uploadProgress = fraction) : undefined;
		/** The response is the updated ticket, thread included. */
		try {
			ticket = await replySupportTicket(params.ticketKey as string, message.body, message.files, onProgress);
			return true;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to send the reply'));
			return false;
		} finally {
			sending = false;
			uploadProgress = null;
		}
	}

	/** Closes or reopens the ticket. */
	async function change(action: 'close' | 'reopen'): Promise<void> {
		changing = true;
		/** Both answer with the updated ticket. */
		try {
			ticket =
				action === 'close'
					? await closeSupportTicket(params.ticketKey as string)
					: await reopenSupportTicket(params.ticketKey as string);
			toastSuccess(action === 'close' ? t2('Ticket closed.') : t2('Ticket reopened.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to update the ticket'));
		} finally {
			changing = false;
		}
	}

	/** Submits the satisfaction rating. */
	async function rate(stars: number, comment: string): Promise<void> {
		rating = true;
		/** A ticket is rated once; the response carries the rating back. */
		try {
			ticket = await rateSupportTicket(params.ticketKey as string, stars, comment);
			toastSuccess(t2('Thank you for your feedback.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to submit the rating'));
		} finally {
			rating = false;
		}
	}

	/** Adds someone by email. Resolves whether it worked, so the card knows to clear its field. */
	async function addPerson(email: string): Promise<boolean> {
		changingPeople = true;
		/** An unknown address comes back as a 400 whose message is worth showing as-is. */
		try {
			ticket = await addSupportParticipant(params.ticketKey as string, email);
			toastSuccess(t2('{{email}} was added to the ticket.', { email }));
			return true;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to add the person'));
			return false;
		} finally {
			changingPeople = false;
		}
	}

	/** Takes a participant off the ticket, as its owner. */
	async function removePerson(person: SupportPerson): Promise<void> {
		changingPeople = true;
		/** The owner removing someone gets the updated ticket back. */
		try {
			ticket = (await removeSupportParticipant(params.ticketKey as string, person.userUUID)) ?? ticket;
			toastSuccess(t2('{{name}} was removed from the ticket.', { name: person.name }));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to remove the person'));
		} finally {
			changingPeople = false;
		}
	}

	/** Leaves the ticket, as a participant, and returns to the support home — it is no longer ours to see. */
	async function leave(): Promise<void> {
		changingPeople = true;
		/** The server answers 204; there is no ticket left to show. */
		try {
			await removeSupportParticipant(params.ticketKey as string, $tokenData?.userUUID ?? '');
			toastSuccess(t2('You left the ticket.'));
			push('/Support');
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to leave the ticket'));
		} finally {
			changingPeople = false;
		}
	}

	/** Why the composer is disabled, in the reader's terms. */
	let blockedReason = $derived(
		ticket && !ticket.permissions.canReply && ticket.permissions.replyBlockedReason
			? $t(supportReasonText(ticket.permissions.replyBlockedReason))
			: ''
	);

	/**
	 * The key of the ticket last loaded. A plain variable, not state: the effect
	 * below compares against it, and reading state it also writes is the shape
	 * that loops.
	 */
	let loadedKey: string | undefined | null = null;

	/*
	 * (Re)loads whenever the route names a different ticket. Following a link to a
	 * linked ticket reuses this component with new params, so loading once on mount
	 * would leave the old ticket on screen under the new URL. `load` runs untracked,
	 * so nothing it reads becomes a reason to run again.
	 */
	$effect(() => {
		const key = params.ticketKey;
		/* Same ticket as on screen — nothing to do. */
		if (key === loadedKey) return;
		loadedKey = key;
		untrack(() => load());
	});
</script>

{#if loading}
	<EmptyState variant="loading" />
{:else if error || !ticket}
	<PageHeader title={$t('Ticket')} breadcrumbs={[{ label: $t('Support'), href: '/Support' }, { label: params.ticketKey ?? '' }]} />
	<EmptyState variant="error" description={error}>
		<RoutedButton size="sm" href="/Support">{$t('Back to support')}</RoutedButton>
	</EmptyState>
{:else}
	<PageHeader
		title={ticket.subject}
		breadcrumbs={[{ label: $t('Support'), href: '/Support' }, { label: ticket.ticketKey }]}
	>
		{#snippet descriptionSnippet()}
			<span class="flex flex-wrap items-center gap-2 text-sm font-normal">
				<span class="font-mono">{ticket!.ticketKey}</span>
				<SupportStatusBadge status={ticket!.status} audience="customer" />
				<SupportPriorityBadge priority={ticket!.priority} />
				<span>· {$t('Opened')} <RelativeTime date={ticket!.created} /></span>
			</span>
		{/snippet}
	</PageHeader>

	<div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
		<div class="min-w-0 space-y-6">
			{#if ticket.viewerRelation === 'licenseOwner'}
				<div class="rounded-lg bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-300" role="note">
					<i class="fa-solid fa-user-group me-2"></i>{$t(
						'{{name}} filed this ticket using a seat of your license. You can follow it, but only they can reply.',
						{ name: ticket.requester.name }
					)}
				</div>
			{:else if ticket.viewerRelation === 'participant'}
				<div class="rounded-lg bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-300" role="note">
					<i class="fa-solid fa-user-plus me-2"></i>{$t('{{owner}} added you to this ticket.', {
						owner: ticket.requester.name
					})}
				</div>
			{/if}

			<SupportMessageThread messages={ticket.messages} audience="customer" />

			{#if ticket.status === 'closed' && ticket.isOwnTicket}
				<AbstractCard class="max-w-none">
					<div class="flex flex-wrap items-center gap-3">
						<p class="text-default grow text-sm">
							{$t('This ticket is closed. If the problem is back, open a follow-up and we will pick it up from here.')}
						</p>
						<RoutedButton
							size="sm"
							href="/Support/New/{ticket.application.applicationUUID}?followUp={encodeURIComponent(ticket.ticketKey)}"
						>
							<i class="fa-solid fa-reply me-2"></i>{$t('Open a follow-up')}
						</RoutedButton>
					</div>
				</AbstractCard>
			{:else}
				<SupportComposer
					mode="customer"
					disabled={!ticket.permissions.canReply}
					disabledReason={blockedReason}
					{sending}
				{uploadProgress}
					onsend={send}
				/>
				<SupportCommunityHelp links={communityLinks} />
			{/if}
		</div>

		<aside class="space-y-4">
			<AbstractCard class="max-w-none gap-3 text-sm">
				<p class="card-label">{$t('Details')}</p>
				<div class="flex items-center gap-2">
					<LicenseAppIcon
						iconImageName={ticket.application.iconImageName}
						icon={ticket.application.icon}
						name={ticket.application.name}
						class="h-4 w-4"
					/>
					<span class="font-medium">{ticket.application.name}</span>
				</div>
				<dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
					<dt class="text-default">{$t('Category')}</dt>
					<dd>{ticket.category?.name ?? '—'}</dd>
					<dt class="text-default">{$t('Version')}</dt>
					<dd>{ticket.productVersion || '—'}</dd>
					<dt class="text-default">{$t('System')}</dt>
					<dd>{ticket.operatingSystem || '—'}</dd>
					<dt class="text-default">{$t('Requester')}</dt>
					<dd>{ticket.requester.name}</dd>
					<dt class="text-default">{$t('Agent')}</dt>
					<dd>{ticket.assignee?.name ?? $t('Not yet assigned')}</dd>
				</dl>
				<SupportTicketLinks
					followUpOf={ticket.followUpOf}
					followUps={ticket.followUps}
					basePath="/Support/Tickets"
					audience="customer"
				/>
			</AbstractCard>

			<SupportAttachmentsPanel messages={ticket.messages} audience="customer" />

			<SupportPeopleCard
				requester={ticket.requester}
				participants={ticket.participants}
				canManage={ticket.permissions.canManageParticipants}
				canLeave={ticket.permissions.canLeave}
				busy={changingPeople}
				onadd={addPerson}
				onremove={removePerson}
				onleave={leave}
			/>

			{#if ticket.permissions.canClose || ticket.permissions.canReopen}
				<AbstractCard class="max-w-none gap-2">
					{#if ticket.permissions.canReopen}
						<Button size="sm" color="alternative" disabled={changing} onclick={() => change('reopen')}>
							{#if changing}<Spinner size="4" class="me-2" />{:else}<i class="fa-solid fa-rotate-left me-2"></i>{/if}
							{$t('Reopen ticket')}
						</Button>
					{/if}
					{#if ticket.permissions.canClose}
						<ConfirmButton
							size="sm"
							color="alternative"
							disabled={changing}
							confirmText={$t('Close this ticket? You can open a follow-up later if you need to.')}
							onconfirm={() => change('close')}
						>
							<i class="fa-solid fa-circle-check me-2"></i>{$t('Close ticket')}
						</ConfirmButton>
					{/if}
				</AbstractCard>
			{/if}

			{#if ticket.permissions.canRate || ticket.satisfactionRating !== null}
				<AbstractCard class="max-w-none">
					<SupportRating
						rating={ticket.satisfactionRating}
						comment={ticket.satisfactionComment}
						editable={ticket.permissions.canRate}
						submitting={rating}
						onrate={rate}
					/>
				</AbstractCard>
			{/if}
		</aside>
	</div>
{/if}
